import { useState, useMemo } from 'react';
import { Recipe } from '@/types/api';
import { SearchInput } from '@/components/recipes/SearchInput';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading';
import { 
  RefreshCw, 
  Search,
  X,
  Utensils,
  Check,
  ImageIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  useRandomRecipes, 
  useRecipeSearch,
  useRecipeCategories,
  useFavorites
} from '@/hooks';
interface RecipeSidebarProps {
  onViewRecipe?: (recipe: Recipe) => void;
  onRecipeSelect?: (recipe: Recipe | null) => void;
  selectedRecipe?: Recipe | null;
  isAddingToMealPlan?: boolean;
  addToMealPlanError?: string | null;
  className?: string;
}

type SidebarMode = 'random' | 'search' | 'favorites';

export function RecipeSidebar({
  onViewRecipe: _onViewRecipe,
  onRecipeSelect,
  selectedRecipe,
  isAddingToMealPlan = false,
  addToMealPlanError = null,
  className
}: RecipeSidebarProps) {
  const [mode, setMode] = useState<SidebarMode>('random');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  
  // Note: Favorites functionality will be integrated later

  // Get data based on current mode
  const { 
    data: randomRecipes, 
    isLoading: randomLoading, 
    refetch: refetchRandom 
  } = useRandomRecipes(12, {
    enabled: mode === 'random'
  });

  const { 
    data: searchResults, 
    isLoading: searchLoading 
  } = useRecipeSearch({
    query: searchQuery,
    category: selectedCategory || undefined,
    limit: 20
  });

  const { data: categories = [] } = useRecipeCategories();

  // Get favorites
  const { 
    data: favoritesData, 
    isLoading: favoritesLoading 
  } = useFavorites(1, 50, {
    enabled: mode === 'favorites'
  });

  // Compute current recipes based on mode
  const currentRecipes = useMemo(() => {
    switch (mode) {
      case 'random':
        return randomRecipes || [];
      case 'search':
        return searchResults?.recipes || [];
      case 'favorites':
        return favoritesData?.favorites.map(f => f.recipe) || [];
      default:
        return [];
    }
  }, [mode, randomRecipes, searchResults, favoritesData]);

  const isLoading = mode === 'random' 
    ? randomLoading 
    : mode === 'search' 
    ? searchLoading 
    : favoritesLoading;

  const handleModeChange = (newMode: SidebarMode) => {
    setMode(newMode);
    if (newMode === 'search') {
      setSearchQuery('');
      setSelectedCategory('');
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(selectedCategory === category ? '' : category);
  };

  const handleRefresh = () => {
    if (mode === 'random') {
      refetchRandom();
    }
  };

  return (
    <Card className={cn("flex flex-col h-full", className)}>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Utensils className="h-5 w-5" />
          Recetas Disponibles
        </CardTitle>
        
        {/* Selected Recipe Indicator */}
        {selectedRecipe && (
          <div className={cn(
            "mt-2 p-3 border rounded-lg transition-all duration-200",
            isAddingToMealPlan 
              ? "bg-blue-50 border-blue-200" 
              : addToMealPlanError
              ? "bg-red-50 border-red-200"
              : "bg-primary/5 border-primary/20"
          )}>
            <div className="flex items-center gap-2 text-sm">
              {isAddingToMealPlan ? (
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              ) : addToMealPlanError ? (
                <X className="h-4 w-4 text-red-500" />
              ) : (
                <Check className="h-4 w-4 text-primary" />
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "font-medium",
                    isAddingToMealPlan 
                      ? "text-blue-700" 
                      : addToMealPlanError
                      ? "text-red-700"
                      : "text-primary"
                  )}>
                    {isAddingToMealPlan 
                      ? "Añadiendo..." 
                      : addToMealPlanError
                      ? "Error:"
                      : "Seleccionada:"}
                  </span>
                  <span className="text-gray-700 truncate">{selectedRecipe.title}</span>
                </div>
                
                {addToMealPlanError && (
                  <p className="text-xs text-red-600 mt-1">{addToMealPlanError}</p>
                )}
                
                {!isAddingToMealPlan && !addToMealPlanError && (
                  <p className="text-xs text-gray-600 mt-1">
                    Haz clic en un slot del calendario para planificar
                  </p>
                )}
              </div>
              
              {!isAddingToMealPlan && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRecipeSelect?.(null)}
                  className="h-6 w-6 p-0 flex-shrink-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Mode Tabs */}
        <div className="flex items-center gap-1 mt-3">
          <Button
            variant={mode === 'random' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleModeChange('random')}
            className="text-xs"
          >
            Explorar
          </Button>
          <Button
            variant={mode === 'search' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleModeChange('search')}
            className="text-xs"
          >
            <Search className="h-3 w-3 mr-1" />
            Buscar
          </Button>
          <Button
            variant={mode === 'favorites' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleModeChange('favorites')}
            className="text-xs"
          >
            ❤️ Favoritos
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-3 pb-4">
        {/* Search Mode Controls */}
        {mode === 'search' && (
          <div className="space-y-3">
            {/* Search Input */}
            <SearchInput
              onSearch={handleSearch}
              placeholder="Buscar recetas..."
              debounceMs={500}
              initialValue={searchQuery}
            />
            
            {/* Category Filter */}
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                Categorías
              </div>
              <div className="flex flex-wrap gap-1">
                {categories.slice(0, 8).map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleCategoryChange(category)}
                    className="text-xs h-6"
                  >
                    {category}
                  </Button>
                ))}
              </div>
              
              {/* Clear filters */}
              {(searchQuery || selectedCategory) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('');
                  }}
                  className="text-xs h-6"
                >
                  <X className="h-3 w-3 mr-1" />
                  Limpiar
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Random Mode Controls */}
        {mode === 'random' && (
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-600">
              Selecciona una receta y haz clic en el calendario
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              className="text-xs h-6"
            >
              <RefreshCw className={cn("h-3 w-3", isLoading && "animate-spin")} />
            </Button>
          </div>
        )}

        {/* Favorites Mode Controls */}
        {mode === 'favorites' && (
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-600">
              Tus recetas favoritas listas para planificar
            </div>
            {favoritesData && (
              <div className="text-xs text-gray-500">
                {favoritesData.total} favorita{favoritesData.total !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        )}

        {/* Recipes List */}
        {!isLoading && (
          <div className="flex-1 overflow-y-auto space-y-2 max-h-[400px] lg:max-h-[600px]">
            {currentRecipes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {mode === 'search' && !searchQuery && !selectedCategory ? (
                  <div className="space-y-2">
                    <Search className="h-8 w-8 mx-auto text-gray-300" />
                    <p className="text-sm">Busca recetas para agregar al plan</p>
                  </div>
                ) : mode === 'search' ? (
                  <div className="space-y-2">
                    <Search className="h-8 w-8 mx-auto text-gray-300" />
                    <p className="text-sm">No se encontraron recetas</p>
                    <p className="text-xs text-gray-400">
                      Prueba con otros términos
                    </p>
                  </div>
                ) : mode === 'favorites' ? (
                  <div className="space-y-2">
                    <div className="text-4xl mb-2">❤️</div>
                    <p className="text-sm">No tienes recetas favoritas</p>
                    <p className="text-xs text-gray-400">
                      Explora recetas y márcalas como favoritas
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Utensils className="h-8 w-8 mx-auto text-gray-300" />
                    <p className="text-sm">No hay recetas disponibles</p>
                  </div>
                )}
              </div>
            ) : (
              currentRecipes.map((recipe) => {
                const isSelected = selectedRecipe?.id === recipe.id;
                const isBeingAdded = isSelected && isAddingToMealPlan;
                
                return (
                  <div
                    key={recipe.id}
                    onClick={() => !isAddingToMealPlan && onRecipeSelect?.(recipe)}
                    className={cn(
                      "p-3 rounded-lg border transition-all duration-200",
                      isBeingAdded
                        ? "border-blue-300 bg-blue-50 cursor-wait"
                        : isSelected
                        ? "border-primary bg-primary/5 shadow-md cursor-pointer"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm cursor-pointer",
                      isAddingToMealPlan && !isSelected && "opacity-50"
                    )}
                  >
                  <div className="flex gap-3">
                    {/* Recipe Image */}
                    <div className="flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-gray-100 relative">
                      {recipe.imageUrl ? (
                        <img 
                          src={recipe.imageUrl} 
                          alt={recipe.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="h-4 w-4 text-gray-400" />
                        </div>
                      )}
                      {/* Favorite badge for favorites mode */}
                      {mode === 'favorites' && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-xs text-white">❤️</span>
                        </div>
                      )}
                    </div>

                    {/* Recipe Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 line-clamp-1 mb-1">
                        {recipe.title}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        {recipe.timeMinutes && (
                          <span>{recipe.timeMinutes}min</span>
                        )}
                        <span>{recipe.servings}p</span>
                      </div>
                    </div>

                    {/* Selection indicator */}
                    {isSelected && (
                      <div className={cn(
                        "flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center",
                        isBeingAdded
                          ? "bg-blue-500"
                          : "bg-primary"
                      )}>
                        {isBeingAdded ? (
                          <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        )}

        {/* Results Count */}
        {!isLoading && currentRecipes.length > 0 && (
          <div className="text-center pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              {currentRecipes.length} receta{currentRecipes.length !== 1 ? 's' : ''} disponible{currentRecipes.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
